/**
 * Decode planner (pure — unit tested): which pages get their large-tier image mounted.
 *
 * Priority: visible pages (most visible first) → connector targets of visible pages → ±1 neighbours
 * of visible pages (next before previous; in two-up page mode the next and previous spread). Pages are added while the decoded-megapixel budget
 * allows. If a higher-priority page does not fit, no lower-priority group is considered (memory is
 * never spent on off-screen pages while a visible page shows its placeholder).
 */
export interface DecodePlanInput {
  pageCount: number;
  /** Visible pages, most visible first. */
  visible: readonly number[];
  /** Connector targets of the visible pages. */
  targets: readonly number[];
  /** Neighbour pages to pre-decode last (default: neighboursOf(visible)). */
  neighbours?: readonly number[];
  /** Is the large image of page i available (blob arrived)? */
  available: (page: number) => boolean;
  /** Decoded size of page i's large image in megapixels. */
  megapixels: (page: number) => number;
  budgetMp: number;
}

export function neighboursOf(visible: readonly number[], pageCount: number): number[] {
  const out: number[] = [];
  for (const v of visible) {
    for (const n of [v + 1, v - 1]) if (n >= 0 && n < pageCount) out.push(n);
  }
  return out;
}

export function planDecodes(input: DecodePlanInput): number[] {
  const { pageCount, budgetMp } = input;
  const chosen: number[] = [];
  const seen = new Set<number>();
  let used = 0;
  const groups = [input.visible, input.targets, input.neighbours ?? neighboursOf(input.visible, pageCount)];
  for (const group of groups) {
    let skipped = false;
    for (const p of group) {
      if (!Number.isInteger(p) || p < 0 || p >= pageCount || seen.has(p)) continue;
      seen.add(p);
      if (!input.available(p)) continue;
      const mp = Math.max(0, input.megapixels(p));
      if (used + mp > budgetMp) {
        skipped = true;
        continue;
      }
      used += mp;
      chosen.push(p);
    }
    if (skipped) break;
  }
  return chosen;
}
